import { useState, useEffect } from 'react'
import { siteAPI, uploadMultipleFiles, uploadFile } from '../services/api'
import { useNotification } from '../context/NotificationContext'
import { getErrorMessage, getImageUrl, confirmAction } from '../utils/helpers'

const Carousel = () => {
  const { showSuccess, showError } = useNotification()
  const [carousel, setCarousel] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchCarousel()
  }, [])

  const fetchCarousel = async () => {
    try {
      const response = await siteAPI.getSettings()
      setCarousel(response.data.settings?.carousel || [])
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleUpload = async (e) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    const files = formData.getAll('images')
    
    if (files.length === 0 || (files.length === 1 && files[0].size === 0)) {
      showError('Choose one or more image files to upload.')
      return
    }

    try {
      const uploadedPaths = []
      for (const file of files) {
        if (file.size > 0) {
          const uploadRes = await uploadFile(file)
          uploadedPaths.push(uploadRes.data.filename || uploadRes.data.image)
        }
      }

      const newCarousel = [...uploadedPaths, ...carousel]
      await siteAPI.updateSettings({ carousel: newCarousel })
      showSuccess(`${uploadedPaths.length} carousel image(s) uploaded.`)
      fetchCarousel()
      e.target.reset()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleDelete = async (imagePath) => {
    if (!confirmAction('Delete this carousel image?')) return
    
    try {
      const newCarousel = carousel.filter(img => img !== imagePath)
      await siteAPI.updateSettings({ carousel: newCarousel })
      showSuccess('Carousel image deleted.')
      fetchCarousel()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const moveImage = (from, to) => {
    const newCarousel = [...carousel]
    const [item] = newCarousel.splice(from, 1)
    newCarousel.splice(to, 0, item)
    return newCarousel
  }

  const handleMove = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= carousel.length) return

    try {
      const newCarousel = moveImage(index, newIndex)
      await siteAPI.updateSettings({ carousel: newCarousel })
      setCarousel(newCarousel)
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  if (loading) return <div className="admin-page">Loading...</div>

  return (
    <div className="admin-page">
      <h2 className="admin-title">Homepage Hero / Carousel</h2>
      <p className="admin-sub">Manage carousel images shown on the homepage hero section.</p>

      <div className="row">
        <div className="col-lg-8">
          <div className="panel">
            <div className="panel-head">Carousel Images (in display order)</div>
            {carousel.length > 0 ? (
              carousel.map((image, index) => (
                <div key={index} className="item-row">
                  <img src={getImageUrl(image)} alt="" className="thumb-lg" />
                  <div className="item-info">
                    <div className="item-name">{image}</div>
                    <div className="item-sub">Position: {index + 1}</div>
                  </div>
                  <div className="item-actions">
                    {index > 0 && (
                      <button onClick={() => handleMove(index, 'up')} className="btn btn-sm btn-outline-secondary">
                        <i className="fa fa-arrow-up"></i>
                      </button>
                    )}
                    {index < carousel.length - 1 && (
                      <button onClick={() => handleMove(index, 'down')} className="btn btn-sm btn-outline-secondary">
                        <i className="fa fa-arrow-down"></i>
                      </button>
                    )}
                    <button onClick={() => handleDelete(image)} className="btn btn-sm btn-danger">
                      <i className="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="muted">No carousel images yet.</p>
            )}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="panel">
            <div className="panel-head">Upload Carousel Images</div>
            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label>Images * (up to 10)</label>
                <input type="file" name="images" className="form-control-file" accept="image/*" multiple required />
              </div>
              <p className="muted" style={{fontSize: '12px'}}>
                New uploads appear first on the website. You can reorder them after uploading.
              </p>
              <button className="btn btn-gold btn-block">Upload Images</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Carousel
